import type { AIAnalysisResult } from './ai';
import { getStudyScope, isStudyScopeCurrent, studyStorageKey, type StudyScope } from './studyStorage';
import { remoteAccountDocument, saveAccountDocument } from './accountPersistence';

const key = 'ai_result';
let cached: { scope: StudyScope; result: AIAnalysisResult } | null = null;
let currentCached: typeof cached = null;
const assetKey = (id: string, owner: StudyScope) => studyStorageKey(`generation:${id}`, owner);
const database = () => new Promise<IDBDatabase>((resolve, reject) => {
  let settled = false;
  const fail = () => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    reject(new Error('Saved lesson storage is unavailable. Close other LAVYN tabs and try again.'));
  };
  const timer = setTimeout(fail, 5000);
  let request: IDBOpenDBRequest;
  try { request = indexedDB.open('lavyn_lesson_assets', 1); }
  catch { fail(); return; }
  request.onupgradeneeded = () => request.result.createObjectStore('results');
  request.onsuccess = () => {
    if (settled) { request.result.close(); return; }
    settled = true;
    clearTimeout(timer);
    resolve(request.result);
  };
  request.onerror = fail;
  request.onblocked = fail;
});

export async function loadAIResult(generationId: string): Promise<AIAnalysisResult | null> {
  const owner = getStudyScope();
  if (!owner.ready) return null;
  const remote = remoteAccountDocument(owner.ownerId, `lesson:${generationId}`);
  if (remote) return remote.payload as AIAnalysisResult;
  const current = getAIResult(generationId);
  if (current) return current;
  if (!owner.ownerId) return null;
  const db = await database();
  try {
    const record = await new Promise<{ ownerId: string | null; result: AIAnalysisResult } | undefined>((resolve, reject) => {
      const transaction = db.transaction('results');
      const timer = setTimeout(() => {
        reject(new Error('Opening the saved lesson timed out. Please try again.'));
        try { transaction.abort(); } catch { /* Read may have already finished. */ }
      }, 5000);
      const request = transaction.objectStore('results').get(assetKey(generationId, owner));
      request.onsuccess = () => { clearTimeout(timer); resolve(request.result); };
      request.onerror = () => { clearTimeout(timer); reject(request.error); };
      transaction.onabort = () => { clearTimeout(timer); reject(transaction.error || new Error('Saved lesson read interrupted.')); };
    });
    // Never accept an unowned legacy result, another account, or a different job.
    if (!isStudyScopeCurrent(owner) || !record || record.ownerId !== owner.ownerId ||
        record.result?.id !== generationId) return null;
    cached = { scope: owner, result: record.result };
    if (sessionStorage.getItem(studyStorageKey(key, owner)) === `indexeddb:${generationId}`) currentCached = cached;
    return record.result;
  } finally { db.close(); }
}

export async function hydrateAIResult() {
  const owner = getStudyScope();
  if (!owner.ready || !owner.ownerId) return;
  const marker = sessionStorage.getItem(studyStorageKey(key, owner));
  if (marker?.startsWith('indexeddb:')) await loadAIResult(marker.slice(10));
}

export async function saveAIResult(result: AIAnalysisResult, owner = getStudyScope()) {
  if (!owner.ready) return;
  // Guests never create persistent archives or restorable session snapshots.
  if (!owner.ownerId) {
    if (isStudyScopeCurrent(owner)) {
      cached = { scope: owner, result };
      currentCached = cached;
    }
    return;
  }
  // Commit the durable account record before treating the local archive as saved.
  await saveAccountDocument(owner.ownerId, `lesson:${result.id}`, result);
  // Archive every authenticated lesson, not only the most recent large one.
  const db = await database();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('results', 'readwrite');
      transaction.objectStore('results').put({ ownerId: owner.ownerId, result }, assetKey(result.id, owner));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally { db.close(); }
  if (!isStudyScopeCurrent(owner)) return;
  try {
    sessionStorage.setItem(studyStorageKey(key, owner), JSON.stringify(result));
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') throw error;
    sessionStorage.removeItem('scan_input_image');
    sessionStorage.setItem(studyStorageKey(key, owner), `indexeddb:${result.id}`);
  }
  sessionStorage.removeItem('scan_input_image');
  cached = { scope: owner, result };
  currentCached = cached;
}

export function getAIResult(generationId?: string): AIAnalysisResult | null {
  const owner = getStudyScope();
  if (!owner.ready) return null;
  if (currentCached?.scope === owner && (!generationId || currentCached.result.id === generationId)) return currentCached.result;
  if (generationId && cached?.scope === owner && cached.result.id === generationId) return cached.result;
  if (!owner.ownerId) return null;
  try {
    const raw = sessionStorage.getItem(studyStorageKey(key, owner));
    if (!raw) return null;
    if (raw.startsWith('indexeddb:')) {
      return cached?.scope === owner && cached.result.id === raw.slice(10) &&
        (!generationId || generationId === cached.result.id) ? cached.result : null;
    }
    const result = JSON.parse(raw) as AIAnalysisResult;
    currentCached = { scope: owner, result };
    return !generationId || result.id === generationId ? result : null;
  } catch { return null; }
}

export function clearAIResult() {
  cached = null;
  currentCached = null;
  try { sessionStorage.removeItem(studyStorageKey(key)); } catch { /* Memory is already cleared. */ }
}

/** Only active guest lesson state; never account archives, chats or usage cookies. */
export function clearGuestLesson() {
  const owner = getStudyScope();
  if (!owner.ready || owner.ownerId) return;
  clearAIResult();
  for (const key of ['lavyn_generation_job', 'scan_awaiting_save', 'scan_input_image',
    'scan_input_type', 'ai_request_complete', 'ai_request_failed']) {
    try { sessionStorage.removeItem(key); } catch { /* Do not break navigation if storage is unavailable. */ }
  }
}
